// Google Maps API anahtarı ve SDK yükleyicisi — tek noktadan.
//
// Anahtar build sırasında REACT_APP_GOOGLE_MAPS_API_KEY ile gelir. Gelmezse
// daha önce MapContainer.jsx içinde sabit kodlu olan anahtara düşülür; böylece
// ortam değişkeni tanımlanmamış kurulumlarda haritalar çalışmaya devam eder.
//
// Not: Maps JS anahtarı istemci paketine gömülür, gizli değil. Google Cloud
// Console'da HTTP referrer kısıtı tanımlı olmalıdır.
// DIKKAT: Bu deger prod'da FIILEN kullanilan anahtardir. Onceki fallback
// (...YkEXFQ) Google tarafinda REQUEST_DENIED donuyordu; MapContainer'daki
// sabit anahtar buraya tasinirken .env tanimli olmayan ortamlarda (prod dahil)
// haritalar sessizce olurdu. Dogru cozum REACT_APP_GOOGLE_MAPS_API_KEY'i
// tanimlamaktir; bu yalnizca emniyet agi.
const FALLBACK_KEY = 'AIzaSyDbfUbXsZ_wq9uVD4utalXbnWfhGWZVTIo';

export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || FALLBACK_KEY;

const SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const READY_TIMEOUT_MS = 20000;

let loaderPromise = null;

// window.google.maps hazır olana kadar bekler. MapContainer SDK'yı
// `callback=initMap` ile yüklediği için script etiketinin varlığı tek başına
// yeterli değil; gerçekten hazır olmasını beklemek gerekiyor.
const waitForMapsReady = (resolve, reject) => {
  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (window.google && window.google.maps) {
      clearInterval(timer);
      resolve(window.google.maps);
    } else if (Date.now() - startedAt > READY_TIMEOUT_MS) {
      clearInterval(timer);
      loaderPromise = null;
      reject(new Error('Google Maps yüklenemedi (zaman aşımı).'));
    }
  }, 120);
};

/**
 * Google Maps JS SDK'sını (bir kez) yükler.
 * @param {string} libraries - virgülle ayrılmış kütüphane listesi
 * @returns {Promise<object>} window.google.maps
 */
export const loadGoogleMaps = (libraries = 'places,geometry') => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Maps yalnızca tarayıcıda yüklenebilir.'));
  }
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    // Sayfada zaten bir yükleyici varsa (ör. MapContainer) ikinci script ekleme —
    // Google SDK'nın iki kez yüklenmesi konsolda hata üretir.
    if (document.querySelector(SCRIPT_SELECTOR)) {
      waitForMapsReady(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
      `&libraries=${encodeURIComponent(libraries)}` +
      '&language=tr&region=TR';
    script.async = true;
    script.defer = true;
    script.onload = () => waitForMapsReady(resolve, reject);
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Google Maps script yüklenemedi.'));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
};

/**
 * Verilen koordinatın yakınında Street View panoraması var mı?
 * Kapsama dışındaki (ör. AVM içi) lokasyonlarda null döner; çağıran taraf
 * bu durumda kullanıcıya "görüntü yok" gösterip harici linke düşürebilir.
 * @returns {Promise<object|null>} panorama verisi ya da null
 */
export const findStreetViewPanorama = (lat, lng, radius = 80) =>
  loadGoogleMaps().then(
    (maps) =>
      new Promise((resolve) => {
        try {
          new maps.StreetViewService().getPanorama(
            { location: { lat, lng }, radius },
            (data, status) => resolve(status === maps.StreetViewStatus.OK ? data : null)
          );
        } catch (e) {
          resolve(null);
        }
      })
  );

/** Verilen koordinat için Google Maps Street View bağlantısı. */
export const streetViewLink = (lat, lng, heading = 0, pitch = 0) =>
  'https://www.google.com/maps/@?api=1&map_action=pano' +
  `&viewpoint=${lat},${lng}&heading=${heading}&pitch=${pitch}&fov=90`;

/** Verilen koordinat için Google Maps konum bağlantısı. */
export const mapsLink = (lat, lng) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

export default {
  GOOGLE_MAPS_API_KEY,
  loadGoogleMaps,
  findStreetViewPanorama,
  streetViewLink,
  mapsLink,
};
